import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Mic, Video, Clock, CheckCircle2 } from 'lucide-react';

const translations = {
  en: {
    welcome: 'Welcome to Your Expert Interview',
    thankYou: 'Thank you for participating,',
    interviewDetails: 'Interview Details',
    yourRole: 'Your Role',
    estimatedDuration: 'Estimated Duration',
    duration: '20-30 minutes',
    whatToExpect: 'What to Expect',
    step1Title: 'Review the Briefing',
    step1Desc: 'Get context about the interview topics and questions',
    step2Title: 'Setup Your Equipment',
    step2Desc: 'Test your camera and microphone to ensure quality',
    step3Title: 'Conduct the Interview',
    step3Desc: 'Answer questions naturally - your AI journalist will guide you',
    step4Title: 'Review & Submit',
    step4Desc: 'Your recording will be processed into a professional article',
    technicalRequirements: 'Technical Requirements',
    reqCamera: 'Working camera and microphone',
    reqInternet: 'Stable internet connection',
    reqEnvironment: 'Quiet environment with good lighting',
    reqBrowser: 'Chrome, Firefox, or Safari browser',
    getStarted: 'Get Started',
    at: 'at'
  },
  es: {
    welcome: 'Bienvenido a tu Entrevista de Experto',
    thankYou: 'Gracias por participar,',
    interviewDetails: 'Detalles de la Entrevista',
    yourRole: 'Tu Rol',
    estimatedDuration: 'Duración Estimada',
    duration: '20-30 minutos',
    whatToExpect: 'Qué Esperar',
    step1Title: 'Revisar el Briefing',
    step1Desc: 'Obtén contexto sobre los temas y preguntas de la entrevista',
    step2Title: 'Configurar tu Equipo',
    step2Desc: 'Prueba tu cámara y micrófono para asegurar la calidad',
    step3Title: 'Realizar la Entrevista',
    step3Desc: 'Responde las preguntas de forma natural - tu periodista IA te guiará',
    step4Title: 'Revisar y Enviar',
    step4Desc: 'Tu grabación será procesada en un artículo profesional',
    technicalRequirements: 'Requisitos Técnicos',
    reqCamera: 'Cámara y micrófono funcionando',
    reqInternet: 'Conexión a internet estable',
    reqEnvironment: 'Ambiente tranquilo con buena iluminación',
    reqBrowser: 'Navegador Chrome, Firefox o Safari',
    getStarted: 'Comenzar',
    at: 'en'
  },
  ca: {
    welcome: 'Benvingut a la teva Entrevista d\'Expert',
    thankYou: 'Gràcies per participar,',
    interviewDetails: 'Detalls de l\'Entrevista',
    yourRole: 'El teu Rol',
    estimatedDuration: 'Durada Estimada',
    duration: '20-30 minuts',
    whatToExpect: 'Què Esperar',
    step1Title: 'Revisar el Briefing',
    step1Desc: 'Obtén context sobre els temes i preguntes de l\'entrevista',
    step2Title: 'Configurar el teu Equip',
    step2Desc: 'Prova la teva càmera i micròfon per assegurar la qualitat',
    step3Title: 'Realitzar l\'Entrevista',
    step3Desc: 'Respon les preguntes de forma natural - el teu periodista IA et guiarà',
    step4Title: 'Revisar i Enviar',
    step4Desc: 'La teva gravació serà processada en un article professional',
    technicalRequirements: 'Requisits Tècnics',
    reqCamera: 'Càmera i micròfon funcionant',
    reqInternet: 'Connexió a internet estable',
    reqEnvironment: 'Ambient tranquil amb bona il·luminació',
    reqBrowser: 'Navegador Chrome, Firefox o Safari',
    getStarted: 'Començar',
    at: 'a'
  }
};

interface ExpertWelcomeScreenProps {
  interview: {
    expertName: string;
    expertTitle: string;
    expertCompany: string;
    language?: string;
    journalist: {
      name: string;
      title: string;
      bio?: string;
      photoUrl?: string;
    };
    siteDomain: string;
  };
  onNext: () => void;
}

export function ExpertWelcomeScreen({ interview, onNext }: ExpertWelcomeScreenProps) {
  const lang = (interview.language || 'en') as keyof typeof translations;
  const t = translations[lang] || translations.en;

  const journalistInitials = interview.journalist.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl">
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-3xl font-bold mb-2">
            {t.welcome}
          </CardTitle>
          <p className="text-lg text-muted-foreground">
            {t.thankYou} {interview.expertName}!
          </p>
        </CardHeader>

        <CardContent className="space-y-8">
          {/* Journalist Introduction */}
          <div className="flex items-start gap-6 p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
            <Avatar className="h-20 w-20 border-4 border-white shadow-lg">
              {interview.journalist.photoUrl && (
                <AvatarImage src={interview.journalist.photoUrl} alt={interview.journalist.name} />
              )}
              <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                {journalistInitials}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-1">{interview.journalist.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{interview.journalist.title}</p>
              {interview.journalist.bio && (
                <p className="text-sm leading-relaxed">{interview.journalist.bio}</p>
              )}
            </div>
          </div>

          {/* Interview Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t.interviewDetails}</h3>
            <div className="grid gap-3">
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{t.yourRole}</p>
                  <p className="text-sm text-muted-foreground">
                    {interview.expertTitle} {t.at} {interview.expertCompany}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{t.estimatedDuration}</p>
                  <p className="text-sm text-muted-foreground">{t.duration}</p>
                </div>
              </div>
            </div>
          </div>

          {/* What to Expect */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t.whatToExpect}</h3>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <div>
                  <p className="font-medium">{t.step1Title}</p>
                  <p className="text-sm text-muted-foreground">
                    {t.step1Desc}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <div>
                  <p className="font-medium">{t.step2Title}</p>
                  <p className="text-sm text-muted-foreground">
                    {t.step2Desc}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <div>
                  <p className="font-medium">{t.step3Title}</p>
                  <p className="text-sm text-muted-foreground">
                    {t.step3Desc}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  4
                </div>
                <div>
                  <p className="font-medium">{t.step4Title}</p>
                  <p className="text-sm text-muted-foreground">
                    {t.step4Desc}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Requirements */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Video className="w-5 h-5 text-blue-600" />
              {t.technicalRequirements}
            </h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>{t.reqCamera}</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>{t.reqInternet}</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>{t.reqEnvironment}</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>{t.reqBrowser}</span>
              </li>
            </ul>
          </div>

          {/* Action Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={onNext}
              size="lg"
              className="px-8"
            >
              {t.getStarted}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
